import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import useAxiosPrivate from './useAxiosPrivate';
import { timeRange, roomLabel } from '../utils/lessonLabels';

// Opens the attendance QR for a lesson. A professor who teaches other subjects at the same time
// (a combined class) is asked which of them the QR covers: one QR, one session per lesson, one
// token. Spec: ops/combined-classes-qr-spec.md
const messageKeys = {
  TOO_EARLY_START: 'tooEarlyToStartSession',
  TOO_LATE_START: 'tooLateToStartSession',
  TOO_EARLY_END: 'tooEarlyToEndSession',
  TOO_LATE_END: 'tooLateToEndSession',
  weekIsReplaced: 'qrLessonReplaced',
  NOT_ASSIGNED: 'qrNotYourLesson',
};

const useStartAttendance = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [choice, setChoice] = useState(null);
  const [showChoice, setShowChoice] = useState(false);
  const [busy, setBusy] = useState(false);

  const timingMessage = (errorCode, message) =>
    messageKeys[errorCode] ? t(messageKeys[errorCode]) : (message || t('sessionTimingError'));

  const showError = (error) => {
    const data = error?.response?.data;
    if (data?.errorCode) {
      const text = timingMessage(data.errorCode, data.message);
      enqueueSnackbar(data.subjectCode ? `${data.subjectCode}: ${text}` : text, { variant: 'warning' });
    } else {
      enqueueSnackbar(t('errorStartingSession'), { variant: 'error' });
    }
  };

  // One QR for these lessons. The server hands back the running QR when it covers exactly them.
  const openQr = async (schedulePlanWeekIds, sessionType = 1, { forceNew = false, replace = false } = {}) => {
    setBusy(true);
    try {
      const response = await axiosPrivate.post('/SchedulePlanWeekSessions/StartCombinedSession', {
        SchedulePlanWeekIds: schedulePlanWeekIds,
        SessionType: sessionType,
        ForceNew: forceNew
      });
      setShowChoice(false);
      navigate(`/attendance/qr?token=${response.data.token}`, { replace });
      return true;
    } catch (error) {
      console.error('Error starting session:', error);
      showError(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const startAttendance = async (schedulePlanWeekId, sessionType = 1) => {
    let lessons;
    try {
      const response = await axiosPrivate.get(`/SchedulePlanWeekSessions/CombinedLessons/${schedulePlanWeekId}`, {
        params: { sessionType }
      });
      lessons = response.data?.lessons || [];
    } catch (error) {
      console.error('Error loading lessons:', error);
      showError(error);
      return;
    }

    // The first lesson is the one clicked; the rest overlap it.
    const [lesson] = lessons;
    if (!lesson) return;
    if (lesson.activeToken) {
      navigate(`/attendance/qr?token=${lesson.activeToken}`);
      return;
    }
    if (!lesson.windowOpen) {
      enqueueSnackbar(timingMessage(lesson.windowErrorCode, lesson.windowMessage), { variant: 'warning' });
      return;
    }
    if (lessons.length === 1) {
      await openQr([lesson.schedulePlanWeekId], sessionType);
      return;
    }
    // Lessons already combined with this one today are part of its QR whatever is ticked: the server
    // locks the class so a new QR never leaves part of an earlier one open.
    setChoice({
      sessionType,
      lessons,
      selected: lessons.filter(l => (l.windowOpen && l.sameRoom) || l.locked).map(l => l.schedulePlanWeekId)
    });
    setShowChoice(true);
  };

  const toggle = (id) => setChoice(current => ({
    ...current,
    selected: current.selected.includes(id)
      ? current.selected.filter(selectedId => selectedId !== id)
      : [...current.selected, id]
  }));

  const close = () => { if (!busy) setShowChoice(false); };

  // In timetable order with the clicked lesson first: the server checks the others against it.
  const selectedIds = choice
    ? choice.lessons.map(l => l.schedulePlanWeekId).filter(id => choice.selected.includes(id))
    : [];

  const attendanceDialog = (
    <Modal show={showChoice} onHide={close} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {choice?.sessionType === 2 ? t('endQr') : t('startQr')} · {t('combinedClass')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t('combinedQrDialogText')}</p>
        {choice?.lessons.some(l => l.locked) && (
          <div className="alert alert-warning py-2 small">
            <i className="bi bi-lock-fill me-2"></i>{t('combinedQrLockedNote')}
          </div>
        )}
        {choice?.lessons.map((lesson, index) => (
          <Form.Check
            key={lesson.schedulePlanWeekId}
            id={`combined-lesson-${lesson.schedulePlanWeekId}`}
            className="mb-3"
            checked={choice.selected.includes(lesson.schedulePlanWeekId)}
            disabled={index === 0 || lesson.locked || !lesson.windowOpen || busy}
            onChange={() => toggle(lesson.schedulePlanWeekId)}
            label={
              <div>
                <div>
                  <strong>{lesson.subjectCode}</strong> {lesson.subjectName}
                  {lesson.scheduleType && <span className="text-muted"> · {lesson.scheduleType}</span>}
                </div>
                <div className="small text-muted">
                  {[timeRange(lesson.startTime, lesson.endTime), roomLabel(lesson) || t('online'), lesson.groups]
                    .filter(Boolean).join(' · ')}
                </div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {index === 0 && <Badge bg="secondary">{t('combinedQrThisLesson')}</Badge>}
                  {lesson.locked && <Badge bg="danger"><i className="bi bi-lock-fill me-1"></i>{t('combinedQrLocked')}</Badge>}
                  {!lesson.sameRoom && <Badge bg="warning" text="dark">{t('combinedQrOtherRoom')}</Badge>}
                  {lesson.activeToken && <Badge bg="info" text="dark">{t('combinedQrAlreadyOpen')}</Badge>}
                </div>
                {!lesson.windowOpen && (
                  <div className="small text-danger mt-1">{timingMessage(lesson.windowErrorCode, lesson.windowMessage)}</div>
                )}
              </div>
            }
          />
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={close} disabled={busy}>
          {t('cancel')}
        </Button>
        {!choice?.lessons[0]?.locked && (
          <Button
            variant="outline-danger"
            disabled={busy || !choice}
            onClick={() => openQr([choice.lessons[0].schedulePlanWeekId], choice.sessionType)}
          >
            {t('combinedQrOnlyThis')}
          </Button>
        )}
        <Button
          variant="danger"
          disabled={busy || selectedIds.length < 2}
          onClick={() => openQr(selectedIds, choice.sessionType)}
        >
          <i className="bi bi-qr-code me-1"></i>
          {t('combinedQrOneForAll', { count: selectedIds.length })}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return { startAttendance, openQr, attendanceDialog };
};

export default useStartAttendance;
